import { T_PANEL, T_DOOR, GAP_DOOR, CELL_FILLS } from './schema';

// Раскладка содержимого одной ячейки → плоские модули.
// Входные параметры: rect = { x, y, w, h } в системе координат корпуса,
// cell = { fill, ...options }, body = { width, height, depth, material, color, color_hex }.
// Возвращает массив модулей (плоский формат, совместимый с текущим рендером и экспортами).

const MIN_DRAWER_H = 12;   // минимальная высота ящика (см)
const DEFAULT_DRAWER_H = 20;

// Получить материал/цвет из body
function baseProps(body) {
  return {
    material: body.material || 'mdf',
    color_id: body.color || 'light_oak',
    color_hex: body.color_hex || '#C4A76C',
  };
}

// Распределить высоту ячейки между полками
function evenPositions(count, total) {
  const positions = [];
  const step = total / (count + 1);
  for (let i = 1; i <= count; i++) positions.push(step * i);
  return positions;
}

export function fillingToModules(rect, cell, body, idx = 0) {
  const fill = cell.fill || CELL_FILLS.empty;
  const base = baseProps(body);
  const mods = [];

  switch (fill) {
    case CELL_FILLS.empty:
      break;

    case CELL_FILLS.shelf: {
      mods.push({
        type: 'shelf', name_ru: `Полка (яч. ${idx + 1})`,
        width: rect.w, height: T_PANEL, depth: body.depth - 2,
        x: rect.x, y: rect.y + rect.h / 2 - T_PANEL / 2,
        ...base,
      });
      break;
    }

    case CELL_FILLS.shelves: {
      const count = Math.max(1, cell.count || 2);
      const positions = evenPositions(count, rect.h);
      positions.forEach((p, i) => {
        mods.push({
          type: 'shelf', name_ru: `Полка ${i + 1} (яч. ${idx + 1})`,
          width: rect.w, height: T_PANEL, depth: body.depth - 2,
          x: rect.x, y: rect.y + p - T_PANEL / 2,
          ...base,
        });
      });
      break;
    }

    case CELL_FILLS.rod: {
      // штанга на ~10 см ниже верха ячейки, опц. полка прямо над ней
      mods.push({
        type: 'hanging_rod', name_ru: `Штанга (яч. ${idx + 1})`,
        width: rect.w, height: 3, depth: 3,
        x: rect.x, y: rect.y + Math.min(12, rect.h * 0.1),
        material: 'metal', color_id: 'gray', color_hex: '#9E9E9E',
      });
      if (cell.hasShelfAbove !== false) {
        mods.push({
          type: 'shelf', name_ru: `Полка над штангой (яч. ${idx + 1})`,
          width: rect.w, height: T_PANEL, depth: body.depth - 2,
          x: rect.x, y: rect.y + Math.max(0, Math.min(12, rect.h * 0.1) - 5),
          ...base,
        });
      }
      break;
    }

    case CELL_FILLS.door: {
      // Накладная дверь по умолчанию: перекрывает ячейку + зазоры
      const overlay = cell.overlay !== false; // default true = накладная
      const hinge = cell.hinge || 'L';        // 'L' | 'R'
      const doorW = overlay ? rect.w + T_PANEL * 2 - GAP_DOOR * 2 : rect.w - GAP_DOOR * 2;
      const doorH = overlay ? rect.h + T_PANEL * 2 - GAP_DOOR * 2 : rect.h - GAP_DOOR * 2;
      const doorX = overlay ? rect.x - T_PANEL + GAP_DOOR : rect.x + GAP_DOOR;
      const doorY = overlay ? rect.y - T_PANEL + GAP_DOOR : rect.y + GAP_DOOR;
      mods.push({
        type: 'door', name_ru: `Дверь (яч. ${idx + 1})`,
        width: doorW, height: doorH, depth: T_DOOR,
        x: doorX, y: doorY,
        hinge, overlay, open: 0,            // новые поля: навес и флаг открытой
        hardware: ['handle_modern', 'hinge_soft'],
        ...base, material: 'mdf',
      });
      break;
    }

    case CELL_FILLS.flap_door: {
      const overlay = cell.overlay !== false;
      const doorW = overlay ? rect.w + T_PANEL * 2 - GAP_DOOR * 2 : rect.w - GAP_DOOR * 2;
      const doorH = overlay ? rect.h + T_PANEL * 2 - GAP_DOOR * 2 : rect.h - GAP_DOOR * 2;
      const doorX = overlay ? rect.x - T_PANEL + GAP_DOOR : rect.x + GAP_DOOR;
      const doorY = overlay ? rect.y - T_PANEL + GAP_DOOR : rect.y + GAP_DOOR;
      mods.push({
        type: 'door', name_ru: `Откидная дверь (яч. ${idx + 1})`,
        width: doorW, height: doorH, depth: T_DOOR,
        x: doorX, y: doorY,
        hinge: 'T', overlay, open: 0, flap: true,
        hardware: ['handle_modern', 'hinge_soft'],
        ...base, material: 'mdf',
      });
      break;
    }

    case CELL_FILLS.drawer: {
      const front = cell.overlay !== false
        ? { w: rect.w + T_PANEL * 2 - GAP_DOOR * 2, h: rect.h + T_PANEL * 2 - GAP_DOOR * 2,
            x: rect.x - T_PANEL + GAP_DOOR, y: rect.y - T_PANEL + GAP_DOOR }
        : { w: rect.w - GAP_DOOR * 2, h: rect.h - GAP_DOOR * 2,
            x: rect.x + GAP_DOOR, y: rect.y + GAP_DOOR };
      mods.push({
        type: 'drawer', name_ru: `Ящик (яч. ${idx + 1})`,
        width: front.w, height: front.h, depth: body.depth - 5,
        x: front.x, y: front.y, open: 0,
        hardware: ['handle_modern', 'slide_ball'],
        ...base, material: 'mdf',
      });
      break;
    }

    case CELL_FILLS.drawers: {
      const count = Math.max(1, cell.count || 3);
      const drawerH = Math.max(MIN_DRAWER_H, rect.h / count);
      for (let i = 0; i < count; i++) {
        const y = rect.y + i * drawerH;
        const overlay = cell.overlay !== false;
        const front = overlay
          ? { w: rect.w + T_PANEL * 2 - GAP_DOOR * 2, h: drawerH - GAP_DOOR * 2,
              x: rect.x - T_PANEL + GAP_DOOR, y: y + GAP_DOOR }
          : { w: rect.w - GAP_DOOR * 2, h: drawerH - GAP_DOOR * 2,
              x: rect.x + GAP_DOOR, y: y + GAP_DOOR };
        mods.push({
          type: 'drawer', name_ru: `Ящик ${i + 1} (яч. ${idx + 1})`,
          width: front.w, height: front.h, depth: body.depth - 5,
          x: front.x, y: front.y, open: 0,
          hardware: ['handle_modern', 'slide_ball'],
          ...base, material: 'mdf',
        });
      }
      break;
    }

    default:
      break;
  }

  return mods;
}
